import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Wand2, Check, Image as ImageIcon, MessageSquareText,
  Video, Copy, Megaphone, Loader2,
} from "lucide-react";
import DOMPurify from "dompurify";
import { api, apiError, BACKEND_URL } from "../../lib/api";
import { ActiveCompanyBadge } from "../../components/ActiveCompanyBadge";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";

function Markdown({ text }) {
  // lightweight markdown -> html for headings, bold, lists
  const html = (text || "")
    .replace(/^## (.*)$/gim, "<h2>$1</h2>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/^[-*] (.*)$/gim, "<li>$1</li>")
    .replace(/(<li>[\s\S]*?<\/li>)/g, "<ul>$1</ul>")
    .replace(/\n{2,}/g, "</p><p>")
    .replace(/\n/g, "<br/>");
  return <div className="markdown-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p>${html}</p>`) }} />;
}

export default function Generator() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [concept, setConcept] = useState(null);
  const [loadingConcept, setLoadingConcept] = useState(false);

  const [textInstruction, setTextInstruction] = useState("");
  const [textContent, setTextContent] = useState(null);
  const [loadingText, setLoadingText] = useState(false);

  const [imagePrompt, setImagePrompt] = useState("");
  const [imageContent, setImageContent] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);

  const [videoMode, setVideoMode] = useState("text");
  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoContent, setVideoContent] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);

  const [creatingCampaign, setCreatingCampaign] = useState(false);

  const generateConcept = async () => {
    if (!prompt.trim()) return toast.error("Escribe una idea primero");
    setLoadingConcept(true);
    setConcept(null);
    try {
      const { data } = await api.post("/concepts/generate", { prompt });
      setConcept(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoadingConcept(false);
    }
  };

  const approveConcept = async () => {
    try {
      const { data } = await api.post(`/concepts/${concept.concept_id}/approve`);
      setConcept(data);
      toast.success("Concepto aprobado. ¡Ahora genera tu contenido!");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const genText = async () => {
    setLoadingText(true);
    try {
      const { data } = await api.post("/content/text", {
        concept_id: concept?.concept_id,
        instruction: textInstruction,
      });
      setTextContent(data);
      toast.success("Mensaje generado");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoadingText(false);
    }
  };

  const genImage = async () => {
    if (!imagePrompt.trim()) return toast.error("Describe la imagen");
    setLoadingImage(true);
    try {
      const { data } = await api.post("/content/image", { prompt: imagePrompt });
      setImageContent(data);
      toast.success("Imagen generada");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoadingImage(false);
    }
  };

  const genVideo = async () => {
    if (videoMode === "text" && !videoPrompt.trim()) return toast.error("Describe el video");
    if (videoMode === "image" && !imageContent) return toast.error("Primero genera una imagen");
    setLoadingVideo(true);
    setVideoContent(null);
    try {
      const payload = videoMode === "image"
        ? { prompt: videoPrompt, image_content_id: imageContent.content_id }
        : { prompt: videoPrompt };
      const { data } = await api.post("/content/video", payload);
      setVideoContent(data);
      // poll for completion
      const poll = async (id, tries = 0) => {
        if (tries > 60) { setLoadingVideo(false); toast.error("El video está tardando demasiado, revísalo más tarde"); return; }
        try {
          const r = await api.get(`/content/${id}`);
          if (r.data.status === "done") { setVideoContent(r.data); setLoadingVideo(false); toast.success("¡Video listo!"); return; }
          if (r.data.status === "error") { setVideoContent(r.data); setLoadingVideo(false); toast.error("No se pudo generar el video"); return; }
        } catch (err) { console.debug("video poll error:", err); }
        setTimeout(() => poll(id, tries + 1), 5000);
      };
      poll(data.content_id);
    } catch (err) {
      setLoadingVideo(false);
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const createCampaign = async () => {
    const ids = [textContent?.content_id, imageContent?.content_id, videoContent?.status === "done" ? videoContent?.content_id : null].filter(Boolean);
    if (ids.length === 0) return toast.error("Genera al menos un contenido");
    setCreatingCampaign(true);
    try {
      await api.post("/campaigns", {
        title: concept?.prompt?.slice(0, 50) || "Mi campaña",
        concept_id: concept?.concept_id,
        content_ids: ids,
      });
      toast.success("¡Campaña creada! Revísala en 'Campañas'.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setCreatingCampaign(false);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado"); };
  const approved = concept?.approved;

  return (
    <div data-testid="generator-page" className="max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="text-purple-500" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Generador IA</h1>
      </div>
      <p className="text-zinc-400 mb-3">Una idea simple se convierte en una campaña lista para reclutar.</p>
      <div className="mb-8"><ActiveCompanyBadge /></div>

      {/* Step 1 */}
      <section className="rounded-2xl border border-white/10 bg-[#121212] p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold">1</span>
          <h2 className="font-display font-bold text-xl">Tu idea</h2>
        </div>
        <Textarea
          data-testid="idea-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Ej. Quiero atraer mamás que buscan ingresos extra desde casa..."
          rows={3}
          className="bg-black border-white/15 rounded-xl focus-visible:ring-purple-500"
        />
        <Button data-testid="generate-concept-button" onClick={generateConcept} disabled={loadingConcept} className="mt-4 bg-purple-600 hover:bg-purple-500 rounded-xl px-6 py-6">
          {loadingConcept ? <Loader2 className="animate-spin mr-2" size={18} /> : <Wand2 size={18} className="mr-2" />}
          {loadingConcept ? "Creando estrategia..." : "Generar estrategia"}
        </Button>
      </section>

      {/* Concept result */}
      {concept && (
        <section data-testid="concept-result" className="rounded-2xl border border-purple-500/30 bg-[#0e0a16] p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl text-purple-300">Estrategia de conceptualización</h2>
            <button onClick={() => copy(concept.strategy)} className="text-zinc-400 hover:text-white"><Copy size={18} /></button>
          </div>
          <Markdown text={concept.strategy} />
          {!approved ? (
            <Button data-testid="approve-concept-button" onClick={approveConcept} className="mt-5 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-5">
              <Check size={18} className="mr-2" /> Aprobar concepto
            </Button>
          ) : (
            <div className="mt-5 inline-flex items-center gap-2 text-green-400 text-sm font-semibold"><Check size={18} /> Concepto aprobado</div>
          )}
        </section>
      )}

      {/* Step 2: content */}
      {approved && (
        <section className="rounded-2xl border border-white/10 bg-[#121212] p-6 mb-6">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold">2</span>
            <h2 className="font-display font-bold text-xl">¿Qué quieres generar?</h2>
          </div>

          {/* Text */}
          <div className="rounded-xl border border-white/10 p-5 mb-5">
            <div className="flex items-center gap-2 mb-3 text-purple-300"><MessageSquareText size={18} /><span className="font-semibold">Mensaje de texto</span></div>
            <Input
              data-testid="text-instruction-input"
              value={textInstruction}
              onChange={(e) => setTextInstruction(e.target.value)}
              placeholder="Opcional: tono o enfoque del mensaje"
              className="bg-black border-white/15 rounded-xl py-5 focus-visible:ring-purple-500 mb-3"
            />
            <Button data-testid="generate-text-button" onClick={genText} disabled={loadingText} className="bg-purple-600 hover:bg-purple-500 rounded-xl">
              {loadingText ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 size={16} className="mr-2" />} Generar texto
            </Button>
            {textContent && (
              <div className="mt-4 rounded-xl bg-black border border-white/10 p-4">
                <p className="text-zinc-200 whitespace-pre-wrap text-sm">{textContent.text}</p>
                <button onClick={() => copy(textContent.text)} className="mt-3 inline-flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300"><Copy size={14} /> Copiar</button>
              </div>
            )}
          </div>

          {/* Image */}
          <div className="rounded-xl border border-white/10 p-5 mb-5">
            <div className="flex items-center gap-2 mb-3 text-purple-300"><ImageIcon size={18} /><span className="font-semibold">Imagen gráfica</span></div>
            <Input
              data-testid="image-prompt-input"
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              placeholder="Ej. Mujer feliz trabajando desde casa con su laptop"
              className="bg-black border-white/15 rounded-xl py-5 focus-visible:ring-purple-500 mb-3"
            />
            <Button data-testid="generate-image-button" onClick={genImage} disabled={loadingImage} className="bg-purple-600 hover:bg-purple-500 rounded-xl">
              {loadingImage ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 size={16} className="mr-2" />} Generar imagen
            </Button>
            {loadingImage && <p className="text-xs text-zinc-500 mt-2">Generando con IA, esto puede tardar unos segundos...</p>}
            {imageContent && (
              <div className="mt-4">
                <img data-testid="generated-image" src={`${BACKEND_URL}${imageContent.image_url}`} alt="Generada" className="rounded-xl border border-white/10 w-full max-w-md" />
              </div>
            )}
          </div>

          {/* Video */}
          <div className="rounded-xl border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-purple-300"><Video size={18} /><span className="font-semibold">Video corto</span></div>
              <span className="text-[10px] uppercase tracking-wider bg-purple-500/15 text-purple-300 px-2.5 py-1 rounded-full font-bold">Nuevo</span>
            </div>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                data-testid="video-mode-text"
                onClick={() => setVideoMode("text")}
                className={`flex-1 text-sm rounded-lg py-2 border ${videoMode === "text" ? "bg-purple-600 border-purple-500" : "bg-black border-white/15 text-zinc-400"}`}
              >
                Desde texto
              </button>
              <button
                type="button"
                data-testid="video-mode-image"
                disabled={!imageContent}
                onClick={() => setVideoMode("image")}
                className={`flex-1 text-sm rounded-lg py-2 border disabled:opacity-40 ${videoMode === "image" ? "bg-purple-600 border-purple-500" : "bg-black border-white/15 text-zinc-400"}`}
              >
                Animar imagen {imageContent ? "" : "(genera una imagen)"}
              </button>
            </div>
            <Input
              data-testid="video-prompt-input"
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              placeholder={videoMode === "image" ? "Cómo animar la imagen (opcional)" : "Describe tu video de marketing"}
              className="bg-black border-white/15 rounded-xl py-5 focus-visible:ring-purple-500 mb-3"
            />
            <Button data-testid="generate-video-button" onClick={genVideo} disabled={loadingVideo} className="bg-purple-600 hover:bg-purple-500 rounded-xl">
              {loadingVideo ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 size={16} className="mr-2" />} Generar video
            </Button>
            {loadingVideo && <p className="text-xs text-zinc-500 mt-2">Generando video con IA, esto puede tardar 1-3 minutos. No cierres esta página...</p>}
            {videoContent?.status === "error" && <p className="text-xs text-red-400 mt-2">{videoContent.error || "No se pudo generar el video. Intenta de nuevo."}</p>}
            {videoContent?.status === "done" && videoContent.video_url && (
              <div className="mt-4">
                <video data-testid="generated-video" src={videoContent.video_url} controls className="rounded-xl border border-white/10 w-full max-w-md" />
                <a href={videoContent.video_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300">Descargar / abrir video</a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Generator is now a free playground; campaigns are built in the guided flow */}
      {approved && (textContent || imageContent || videoContent?.status === "done") && (
        <section className="rounded-2xl border border-yellow-400/30 bg-[#16130a] p-6">
          <div className="flex items-center gap-2 mb-2">
            <Megaphone size={20} className="text-yellow-400" />
            <h2 className="font-display font-bold text-xl">¿Listo para lanzar?</h2>
          </div>
          <p className="text-zinc-400 text-sm mb-4">Crea una campaña guiada paso a paso (WhatsApp o Redes Sociales) con publicación e instrucciones anti-spam.</p>
          <Button data-testid="go-campaigns-button" onClick={() => navigate("/dashboard/campanas")} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-6">
            <Megaphone size={18} className="mr-2" /> Ir a Campañas
          </Button>
        </section>
      )}
    </div>
  );
}
